/**
 * Client for the Student Service backend (`studentservice` repo).
 *
 * Calls go to `VITE_STUDENT_SERVICE_URL`, or to the local backend at
 * `http://localhost:8080/api/v1` when it is not set.
 */

import { freshKeycloakToken, keycloakAuth, keycloakSessionLost, keycloakToken } from '../auth/keycloak'

export const STUDENT_SERVICE_URL: string = import.meta.env.VITE_STUDENT_SERVICE_URL || 'http://localhost:8080/api/v1'

export interface StudentServiceUser {
  id: number
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  role: 'STUDENT' | 'TEACHER' | 'ADMIN'
  /** Absent or null for accounts without a college. */
  collegeId?: number | null
  /** TEACHER accounts only: the mentor record the login belongs to. */
  mentorId?: string | null
  /** STUDENT accounts: picked in onboarding. */
  skills?: string[] | null
  /** STUDENT accounts: absent or null until they finish onboarding. */
  careerGoal?: string | null
  /** Absent when the account has none. */
  bio?: string | null
}

/** What a student can change on their profile. A field left out stays as it is; an empty bio clears it. */
export interface ProfileChanges {
  firstName?: string
  lastName?: string
  bio?: string
  careerGoal?: string
}

export interface StudentServiceSession {
  token: string
  user: StudentServiceUser
}

export interface RosterStudent {
  id: number
  name: string
  email: string
  collegeId: number
  collegeName: string | null
  courseCount: number
  avgProgress: number
  avgAssessmentScore: number
  xp: number
  status: 'active' | 'inactive'
  /** "healthy" or "at-risk". */
  risk: string
}

export class StudentServiceError extends Error {
  constructor(message: string, readonly status: number, readonly body?: unknown) {
    super(message)
    this.name = 'StudentServiceError'
  }
}

let onSessionExpired = () => {
  if (keycloakAuth) return keycloakSessionLost()
  try {
    localStorage.removeItem('userSession')
  } catch {
    // Storage blocked: the redirect below still lands on the login page.
  }
  window.location.assign('/login')
}

/** Sends the user to sign in again, for a caller that saw the backend reject the session's token. */
export function sessionExpired(): void {
  onSessionExpired()
}

/** Replaces what happens when the backend rejects the session's token; tests use it to avoid a real redirect. */
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler
}

function url(path: string): string {
  return `${STUDENT_SERVICE_URL}${path}`
}

async function request<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  // A file upload sets its own multipart Content-Type, boundary included.
  const headers: Record<string, string> = {
    ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(init.headers as Record<string, string>),
  }
  if (token) headers.Authorization = `Bearer ${token}`
  const response = await fetch(url(path), { ...init, headers })
  if (!response.ok) {
    // A rejected token means the session is no longer valid (expired, or the backend lost it): send the user to sign in again.
    if (response.status === 401 && token) onSessionExpired()
    const body = await response.json().catch(() => null)
    throw new StudentServiceError(body?.error ?? body?.message ?? `Request failed (${response.status})`, response.status, body)
  }
  if (response.status === 204) return undefined as T
  // An empty body (a void endpoint's 200) is undefined. Anything else must be JSON, so an HTML fallback page still throws.
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** The signed-in session's token, or null when nobody is signed in. */
export function sessionToken(): string | null {
  if (keycloakAuth) return keycloakToken()
  try {
    const stored = localStorage.getItem('userSession')
    return stored ? ((JSON.parse(stored).token as string | undefined) ?? null) : null
  } catch {
    return null
  }
}

/** The signed-in session's token, renewed first under Keycloak; throws when nobody is signed in. */
export async function requireToken(): Promise<string> {
  const token = keycloakAuth ? await freshKeycloakToken() : sessionToken()
  if (!token) throw new Error('Your session has ended. Please sign in again.')
  return token
}

/** A request made with the signed-in user's token. */
export async function authed<T>(path: string, init: RequestInit = {}): Promise<T> {
  return request<T>(path, init, await requireToken())
}

/** The signed-in account, for a Keycloak token. A 403 means the account is deactivated or has no role here. */
export function me(token: string): Promise<StudentServiceUser> {
  return request<StudentServiceUser>('/auth/me', {}, token)
}

/** Saves the signed-in student's onboarding answers and returns their updated account. */
export function saveOnboarding(skills: string[], careerGoal: string): Promise<StudentServiceUser> {
  return authed<StudentServiceUser>('/me/onboarding', { method: 'PUT', body: JSON.stringify({ skills, careerGoal }) })
}

/** Saves changes to the signed-in student's profile and returns their updated account. */
export function updateProfile(changes: ProfileChanges): Promise<StudentServiceUser> {
  return authed<StudentServiceUser>('/me/profile', { method: 'PATCH', body: JSON.stringify(changes) })
}

export function login(email: string, password: string): Promise<StudentServiceSession> {
  return request<{ token: string; user: StudentServiceUser }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }).then(({ token, user }) => ({ token, user }))
}

/** First sign-in, step one: the server emails a set-up link if the email is on a roster, and answers the same either way. */
export async function requestActivationLink(email: string): Promise<void> {
  await request<{ message: string }>('/auth/activate/request', { method: 'POST', body: JSON.stringify({ email }) })
}

/** First sign-in, step two: `code` is the token from the emailed link. */
export function activate(code: string, password: string, phoneNumber: string): Promise<StudentServiceSession> {
  return request<{ token: string; user: StudentServiceUser }>('/auth/activate', {
    method: 'POST',
    body: JSON.stringify({ token: code, password, phoneNumber }),
  }).then(({ token, user }) => ({ token, user }))
}

export function logout(token: string): Promise<void> {
  return request<void>('/auth/logout', { method: 'POST' }, token)
}

/** One page of roster students. The server limits a college admin to their own college. */
export async function getRoster(token: string, collegeId?: number): Promise<RosterStudent[]> {
  const params = new URLSearchParams({ size: '200', sort: 'name' })
  if (collegeId !== undefined) params.set('collegeId', String(collegeId))
  const page = await request<{ content: RosterStudent[] }>(`/admin/students?${params}`, {}, token)
  return page.content
}
